// A generated section as DXF geometry, for "File → Export to DXF": cut faces as
// filled SOLIDs, what stands beyond the plane as its visible lines only (hidden
// lines removed), and the section's marker — all at the place the section
// drawing sits in the model. Pure and DOM-free (test/dxfSection.test.mjs).
//
// It reads `sectionPieces` — every draw call of the section, in final paint
// order, with its whole geometry — so the file can never disagree with what is
// drawn: a piece is hidden exactly where a later OPAQUE piece covers it.

import type {
  Polygon,
  ProductInstance,
  SectionLine,
  Vec2,
  Wall,
} from '../shared/types';
import {
  type DxfEntity,
  type DxfHatch,
  rgbToAci,
} from '../shared/dxf';
import { earClip } from '../shared/triangulate';
import {
  type SecPt,
  SECTION_MARKER,
  sectionFrame,
  type SectionPiece,
  sectionPieces,
  type SectionPipe,
  sectionTitlePos,
  sectionView,
  sectionViewExtent,
} from './section';
import { paintsOpaque } from './selfOcclusion';

export { paintsOpaque };

/** A section's entities go on four sub-layers of its layer (`<layer>-CUT` …):
 *  the cut faces, the elevation lines, the glass and the marker. */
export type SectionSubLayer = 'CUT' | 'ELEV' | 'SGLASS' | 'MARK';

/** The marker's sizes in model mm (the canvas's are screen px). */
export const SECTION_ARROW_MM = 300;
export const SECTION_TEXT_MM = 250;

/** A point this close to an occluder's outline is not hidden by it, so a line
 *  along an occluder's edge stays — on every side alike. Two runs this close are
 *  one line. */
const EDGE_TOL = 0.01;

type TH = Pick<SecPt, 't' | 'h' | 'x'>;

const lerp = (a: TH, b: TH, s: number): TH => (s === 0 ? a : s === 1 ? b : { t: a.t + (b.t - a.t) * s, h: a.h + (b.h - a.h) * s });
interface Occluder { ring: TH[]; t0: number; t1: number; h0: number; h1: number }

/** How each kind of piece is written — keyed by every style a piece can have,
 *  so a new `SecDraw` member or `PolyStyle` does not compile until it is placed
 *  (whether it is opaque is `paintsOpaque`'s table, keyed the same way).
 *  `cut`: a cut face, filled as a SOLID; `stroke`: its closed outline or open
 *  line and the sub-layer it goes on, or nothing (a roof piece: its edges come
 *  as lines). `boundary`: an opaque band strokes only where its colour ENDS —
 *  the edges no other stroke draws and no band of its colour continues across
 *  (a wall's top under a ceiling edge of its own colour). A clip edge (`x`) is
 *  never stroked; a repeat (`r`, a mask's stretch of its call's own edge) is
 *  written last, only where no other piece wrote that line — where something
 *  painted between the mask and its call hid the call's own edge. */
interface StyleRule { cut?: true; stroke: { layer: SectionSubLayer; closed: boolean; boundary?: true } | null }
const RULES: Record<SectionPiece['style'], StyleRule> = {
  profile: { cut: true, stroke: { layer: 'CUT', closed: true } },
  cut: { cut: true, stroke: { layer: 'CUT', closed: true } },
  'cut-hatched': { cut: true, stroke: { layer: 'CUT', closed: true } },
  elev: { stroke: { layer: 'ELEV', closed: true } },
  roof: { stroke: null },
  band: { stroke: { layer: 'ELEV', closed: true, boundary: true } },
  outline: { stroke: { layer: 'ELEV', closed: true } },
  window: { stroke: { layer: 'ELEV', closed: true } },
  glass: { stroke: { layer: 'SGLASS', closed: true } },
  vedge: { stroke: { layer: 'ELEV', closed: false } },
  line: { stroke: { layer: 'ELEV', closed: false } },
};

function ringArea(r: readonly { x: number; y: number }[]): number {
  let s = 0;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) s += r[j].x * r[i].y - r[i].x * r[j].y;
  return s / 2;
}

/** The points with consecutive repeats (and a closing repeat) dropped; a merged
 *  point's edge onward is the later one's (its clip flag with it). */
function dedupe(pts: readonly TH[], closed: boolean): TH[] {
  const out: TH[] = [];
  const same = (a: TH, b: TH): boolean => Math.abs(a.t - b.t) < 1e-9 && Math.abs(a.h - b.h) < 1e-9;
  for (const p of pts) {
    const q = out[out.length - 1];
    if (q && same(p, q)) { q.x = p.x; continue; }
    out.push({ t: p.t, h: p.h, ...(p.x ? { x: true } : {}) });
  }
  if (closed) while (out.length > 1 && same(out[0], out[out.length - 1])) out.pop();
  return out;
}

function occluderOf(ring: readonly TH[]): Occluder {
  let t0 = Infinity, t1 = -Infinity, h0 = Infinity, h1 = -Infinity;
  for (const p of ring) { t0 = Math.min(t0, p.t); t1 = Math.max(t1, p.t); h0 = Math.min(h0, p.h); h1 = Math.max(h1, p.h); }
  return { ring: dedupe(ring, true), t0, t1, h0, h1 };
}

function segDist(p: TH, a: TH, b: TH): number {
  const dx = b.t - a.t, dy = b.h - a.h, L2 = dx * dx + dy * dy;
  const s = L2 > 0 ? Math.max(0, Math.min(1, ((p.t - a.t) * dx + (p.h - a.h) * dy) / L2)) : 0;
  return Math.hypot(p.t - (a.t + dx * s), p.h - (a.h + dy * s));
}

/** Strictly inside the occluder: inside it, and more than EDGE_TOL off its outline. */
function strictlyInside(o: Occluder, p: TH): boolean {
  if (p.t <= o.t0 + EDGE_TOL || p.t >= o.t1 - EDGE_TOL || p.h <= o.h0 + EDGE_TOL || p.h >= o.h1 - EDGE_TOL) return false;
  const r = o.ring;
  let inside = false;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
    const a = r[i], b = r[j];
    if (segDist(p, a, b) <= EDGE_TOL) return false;
    if ((a.h > p.h) !== (b.h > p.h) && p.t < ((b.t - a.t) * (p.h - a.h)) / (b.h - a.h) + a.t) inside = !inside;
  }
  return inside;
}

/** Where along a → b (0…1, exclusive) the run may pass in or out of the
 *  occluder: its crossings with the outline, and the outline's corners on it. */
function cutsOf(a: TH, b: TH, o: Occluder): number[] {
  const rx = b.t - a.t, ry = b.h - a.h, L2 = rx * rx + ry * ry, L = Math.sqrt(L2);
  const out: number[] = [];
  const r = o.ring;
  for (let i = 0; i < r.length; i++) {
    const c = r[i], d = r[(i + 1) % r.length];
    const sx = d.t - c.t, sy = d.h - c.h, qx = c.t - a.t, qy = c.h - a.h;
    const den = rx * sy - ry * sx;
    if (Math.abs(den) > 1e-12 * L * Math.hypot(sx, sy)) {
      const s = (qx * sy - qy * sx) / den, u = (qx * ry - qy * rx) / den;
      if (u >= -1e-9 && u <= 1 + 1e-9 && s > 0 && s < 1) out.push(s);
    }
    const s = (qx * rx + qy * ry) / L2;
    if (s > 0 && s < 1 && Math.abs(qx * ry - qy * rx) / L <= 2 * EDGE_TOL) out.push(s);
  }
  return out.sort((x, y) => x - y);
}

type Spans = Array<[number, number]>;

/** What stays of a → b once every occluder is taken out, as parameter spans. */
function visibleSpans(a: TH, b: TH, occ: readonly Occluder[]): Spans {
  let vis: Spans = [[0, 1]];
  const t0 = Math.min(a.t, b.t) - EDGE_TOL, t1 = Math.max(a.t, b.t) + EDGE_TOL;
  const h0 = Math.min(a.h, b.h) - EDGE_TOL, h1 = Math.max(a.h, b.h) + EDGE_TOL;
  const at = (s: number): TH => ({ t: a.t + (b.t - a.t) * s, h: a.h + (b.h - a.h) * s });
  for (const o of occ) {
    if (!vis.length) break;
    if (o.t1 < t0 || o.t0 > t1 || o.h1 < h0 || o.h0 > h1) continue;
    const cuts = cutsOf(a, b, o);
    const next: Spans = [];
    for (const [s0, s1] of vis) {
      const ss = [s0, ...cuts.filter((c) => c > s0 + 1e-12 && c < s1 - 1e-12), s1];
      for (let k = 0; k + 1 < ss.length; k++) {
        const u0 = ss[k], u1 = ss[k + 1];
        if (strictlyInside(o, at((u0 + u1) / 2))) continue;
        const last = next[next.length - 1];
        if (last && last[1] === u0) last[1] = u1; else next.push([u0, u1]);
      }
    }
    vis = next;
  }
  return vis;
}

/** `vis` with the spans `drop` takes out. */
function subtractSpans(vis: Spans, drop: Spans): Spans {
  if (!drop.length) return vis;
  const d = drop.slice().sort((x, y) => x[0] - y[0]);
  const out: Spans = [];
  for (const [s0, s1] of vis) {
    let cur = s0;
    for (const [d0, d1] of d) {
      if (d1 <= cur || d0 >= s1) continue;
      if (d0 > cur) out.push([cur, d0]);
      cur = Math.max(cur, d1);
      if (cur >= s1) break;
    }
    if (cur < s1) out.push([cur, s1]);
  }
  return out;
}

/** A run written (or about to be): its ends, box, sub-layer and colour. */
interface Seg { a: TH; b: TH; t0: number; t1: number; h0: number; h1: number; layer: SectionSubLayer; color?: number }
const segOf = (a: TH, b: TH, layer: SectionSubLayer, color?: number): Seg =>
  ({ a, b, t0: Math.min(a.t, b.t), t1: Math.max(a.t, b.t), h0: Math.min(a.h, b.h), h1: Math.max(a.h, b.h), layer, color });

/** The spans of a → b that lie ALONG one of `segs` (both its ends within
 *  EDGE_TOL of the line a → b) and that `take` accepts. */
function alongSpans(a: TH, b: TH, segs: readonly Seg[], take: (s: Seg, dot: number) => boolean): Spans {
  const dx = b.t - a.t, dy = b.h - a.h, L2 = dx * dx + dy * dy, L = Math.sqrt(L2);
  if (L < 1e-9) return [];
  const t0 = Math.min(a.t, b.t) - EDGE_TOL, t1 = Math.max(a.t, b.t) + EDGE_TOL;
  const h0 = Math.min(a.h, b.h) - EDGE_TOL, h1 = Math.max(a.h, b.h) + EDGE_TOL;
  const off = (p: TH): number => Math.abs((p.t - a.t) * dy - (p.h - a.h) * dx) / L;
  const u = (p: TH): number => ((p.t - a.t) * dx + (p.h - a.h) * dy) / L2;
  const out: Spans = [];
  for (const s of segs) {
    if (s.t1 < t0 || s.t0 > t1 || s.h1 < h0 || s.h0 > h1) continue;
    if (off(s.a) > EDGE_TOL || off(s.b) > EDGE_TOL) continue;
    const ua = u(s.a), ub = u(s.b);
    const u0 = Math.max(0, Math.min(ua, ub)), u1 = Math.min(1, Math.max(ua, ub));
    if ((u1 - u0) * L <= 1e-6) continue;
    if (!take(s, (s.b.t - s.a.t) * dx + (s.b.h - s.a.h) * dy)) continue;
    out.push([u0, u1]);
  }
  return out;
}

/** A stroked outline (`ring`) or line with what later opaque pieces cover and
 *  what `cover` says is drawn already taken out: the visible runs, each a
 *  polyline — the one closed ring while a ring is all still there. */
function visibleRuns(pts: readonly TH[], ring: boolean, occ: readonly Occluder[], cover: (a: TH, b: TH) => Spans): { closed: boolean; runs: TH[][] } {
  const P = dedupe(pts, ring);
  const n = P.length;
  const closed = ring && n >= 3;
  const segs = closed ? n : n - 1;
  if (segs < 1) return { closed, runs: [] };
  const runs: TH[][] = [];
  let cur: TH[] | null = null, joinsNext = false, whole = true, startsAtZero = false;
  for (let k = 0; k < segs; k++) {
    const a = P[k], b = P[(k + 1) % n];
    const len = Math.hypot(b.t - a.t, b.h - a.h);
    const vis = a.x ? [] : subtractSpans(visibleSpans(a, b, occ), cover(a, b)).filter(([s0, s1]) => (s1 - s0) * len > 1e-6);
    if (vis.length !== 1 || vis[0][0] !== 0 || vis[0][1] !== 1) whole = false;
    if (!vis.length || vis[0][0] !== 0) { if (cur) runs.push(cur); cur = null; joinsNext = false; }
    for (const [s0, s1] of vis) {
      if (cur && joinsNext && s0 === 0) cur.push(lerp(a, b, s1));
      else { if (cur) runs.push(cur); cur = [lerp(a, b, s0), lerp(a, b, s1)]; if (k === 0 && s0 === 0) startsAtZero = true; }
      joinsNext = s1 === 1;
    }
    if (!joinsNext && cur) { runs.push(cur); cur = null; }
  }
  if (cur) runs.push(cur);
  if (whole) return { closed, runs: [P] };
  // Round the ring: a run ending on the last corner continues the first.
  if (closed && joinsNext && startsAtZero && runs.length > 1) {
    const last = runs.pop()!;
    runs[0] = [...last, ...runs[0].slice(1)];
  }
  return { closed: false, runs };
}

/** A cut face's SOLIDs: one loop for the writer (which earClips a concave one),
 *  or — with holes, which the writer does not cut out — its triangles. */
export function cutFaceHatches(outer: Vec2[], holes: Vec2[][], color?: number): DxfHatch[] {
  if (!holes.length) return [{ kind: 'hatch', loops: [outer], layer: 'CUT', color }];
  return earClip(outer, holes).map((tri) => ({ kind: 'hatch', loops: [[tri[0], tri[1], tri[2]]], layer: 'CUT', color }));
}

const aci = (hex?: string): number | undefined => (hex ? rgbToAci(hex) : undefined);

/**
 * Every entity of a section, in the model at the place its drawing sits
 * (`sectionView`: world = O + T·t + N·h), on the sub-layers `CUT` / `ELEV` /
 * `SGLASS` / `MARK` (the export puts them under the section's layer). All the
 * SOLIDs come first — R12 has no draw order and many readers paint in file
 * order, so a SOLID written after a visible line would hide it.
 *
 * The strokes are worked out from the LAST piece back, as the screen paints
 * them over each other: each loses what a later opaque piece hides, and what a
 * later piece already writes on its sub-layer in its colour (a face hidden
 * behind a nearer one of the same room leaves no second copy of its outline).
 * A cut face keeps its whole closed outline, the boundary of its SOLID, in the
 * layer's colour so it shows over its own fill.
 */
export function sectionToDxf(sec: SectionLine, label: string, instances: ProductInstance[], walls: Wall[] = [], polygons: Polygon[] = [], pipes: readonly SectionPipe[] = []): DxfEntity[] {
  const view = sectionView(sec);
  const W = (p: TH): Vec2 => ({ x: view.O.x + view.T.x * p.t + view.N.x * p.h, y: view.O.y + view.T.y * p.t + view.N.y * p.h });
  const pieces = sectionPieces(sec, instances, walls, polygons, pipes);
  const opaque = pieces.map((p) => paintsOpaque(p) && p.pts.length >= 3);
  const solids: DxfEntity[] = [];
  pieces.forEach((p) => {
    if (!RULES[p.style].cut || p.pts.length < 3) return;
    const loop = dedupe(p.pts, true).map(W);
    if (loop.length >= 3 && Math.abs(ringArea(loop)) > 1e-6) solids.push(...cutFaceHatches(loop, [], aci(p.color)));
  });
  // An opaque band's edges, with the side its fill lies on, so that two bands of
  // one colour meeting edge to edge (a pier and its sill, the strips a profile
  // is split into) draw no line between them.
  const bands = pieces.map((p, i) => {
    if (!RULES[p.style].stroke?.boundary || !opaque[i]) return null;
    const ring = dedupe(p.pts, true);
    const left = ringArea(ring.map((q) => ({ x: q.t, y: q.h }))) > 0;
    return { color: p.color, left, segs: ring.map((q, k) => segOf(q, ring[(k + 1) % ring.length], 'ELEV')) };
  });
  const seams = (i: number, a: TH, b: TH): Spans => {
    const own = bands[i]!;
    const out: Spans = [];
    bands.forEach((o, j) => {
      if (!o || j === i || o.color !== own.color) return;
      // Opposite sides: o's fill lies left of a → b exactly when its edge runs
      // the same way and its ring turns left, or the other way and it turns right.
      out.push(...alongSpans(a, b, o.segs, (_s, dot) => (o.left === dot > 0) !== own.left));
    });
    return out;
  };
  const written: Seg[] = [];
  const later: Occluder[] = [];
  const strokes: DxfEntity[][] = pieces.map(() => []);
  const emit = (i: number, run: readonly TH[], closed: boolean, layer: SectionSubLayer, color?: number): void => {
    const pts = run.map(W);
    if (pts.length === 2 && !closed) strokes[i].push({ kind: 'line', a: pts[0], b: pts[1], layer, color });
    else if (pts.length >= 2) strokes[i].push({ kind: 'polyline', points: pts, closed, layer, color });
  };
  const repeats: Array<{ i: number; runs: TH[][]; layer: SectionSubLayer; color?: number; cover: (a: TH, b: TH) => Spans }> = [];
  for (let i = pieces.length - 1; i >= 0; i--) {
    const p = pieces[i], st = RULES[p.style].stroke;
    if (st && (!st.boundary || opaque[i])) {
      const color = st.layer === 'CUT' ? undefined : aci(p.color);
      const cover = (a: TH, b: TH): Spans => st.layer === 'CUT' ? []
        : st.boundary ? [...alongSpans(a, b, written, () => true), ...seams(i, a, b)]
        : alongSpans(a, b, written, (s) => s.layer === st.layer && s.color === color);
      const rep = p.pts.some((q) => q.r);
      const { closed, runs } = visibleRuns(rep ? p.pts.map((q) => (q.r ? { ...q, x: true } : q)) : p.pts, st.closed, later, cover);
      const fresh: Seg[] = [];
      for (const run of runs) {
        for (let k = 0; k + 1 < run.length; k++) fresh.push(segOf(run[k], run[k + 1], st.layer, color));
        if (closed) fresh.push(segOf(run[run.length - 1], run[0], st.layer, color));
        emit(i, run, closed, st.layer, color);
      }
      written.push(...fresh);
      if (rep) repeats.push({ i, runs: visibleRuns(p.pts.map((q) => (q.r ? q : { ...q, x: true })), st.closed, later, () => []).runs, layer: st.layer, color, cover });
    }
    if (opaque[i]) later.push(occluderOf(p.pts));
  }
  // What of a repeat is seen and no other piece wrote: its call's own edge,
  // where something painted between the call and its mask hid it.
  for (const { i, runs, layer, color, cover } of repeats) {
    for (const run of runs) {
      let cur: TH[] = [];
      const flush = (): void => { if (cur.length >= 2) emit(i, cur, false, layer, color); cur = []; };
      for (let k = 0; k + 1 < run.length; k++) {
        const a = run[k], b = run[k + 1], len = Math.hypot(b.t - a.t, b.h - a.h);
        for (const [s0, s1] of subtractSpans([[0, 1]], cover(a, b))) {
          if ((s1 - s0) * len <= 1e-6) continue;
          const p0 = lerp(a, b, s0), p1 = lerp(a, b, s1);
          if (!(s0 === 0 && cur[cur.length - 1] === a)) { flush(); cur.push(p0); }
          cur.push(p1);
          written.push(segOf(p0, p1, layer, color));
        }
      }
      flush();
    }
  }
  // The ground line runs the whole cut, never hidden.
  const lines: DxfEntity[] = [{ kind: 'line', a: W({ t: 0, h: 0 }), b: W({ t: view.len, h: 0 }), layer: 'ELEV' }, ...strokes.flat()];
  const mark = sectionMarker(sec, label, W, view.len, sectionViewExtent(sec, instances, walls, polygons, pipes));
  return [...solids, ...mark.solids, ...lines, ...mark.lines];
}

/** The cut line (dash-dot), its two view arrows and the letters — where the
 *  canvas draws them: the letter just off the line's first end, away from the
 *  view, and the `A-A` title centred under the drawing. */
function sectionMarker(sec: SectionLine, label: string, W: (p: TH) => Vec2, len: number, ext: { h0: number; h1: number }): { solids: DxfEntity[]; lines: DxfEntity[] } {
  const { n } = sectionFrame(sec);
  const solids: DxfEntity[] = [];
  const lines: DxfEntity[] = [{ kind: 'polyline', points: [{ ...sec.a }, { ...sec.b }], closed: false, layer: 'MARK', ltype: 'DASHDOT' }];
  // The canvas's arrow (SECTION_MARKER, screen px) is SECTION_ARROW_MM here;
  // its head and the letter's offset scale with it.
  const k = SECTION_ARROW_MM / SECTION_MARKER.arrow;
  for (const e of [sec.a, sec.b]) {
    const tip = { x: e.x + n.x * SECTION_ARROW_MM, y: e.y + n.y * SECTION_ARROW_MM };
    lines.push({ kind: 'line', a: { ...e }, b: tip, layer: 'MARK' });
    const back = { x: tip.x - n.x * SECTION_MARKER.head * k, y: tip.y - n.y * SECTION_MARKER.head * k };
    const side = { x: -n.y * SECTION_MARKER.headHalf * k, y: n.x * SECTION_MARKER.headHalf * k };
    solids.push({ kind: 'hatch', loops: [[tip, { x: back.x + side.x, y: back.y + side.y }, { x: back.x - side.x, y: back.y - side.y }]], layer: 'MARK' });
  }
  if (label) {
    const H = SECTION_TEXT_MM;
    // The letter centred off the first end, the title hanging from where the
    // canvas puts it (`sectionTitlePos`: moved, or deleted) — both upright,
    // whatever the view's turn.
    const at = { x: sec.a.x - n.x * SECTION_MARKER.letter * k, y: sec.a.y - n.y * SECTION_MARKER.letter * k };
    lines.push({ kind: 'text', pos: { x: at.x, y: at.y - H / 2 }, height: H, text: label, layer: 'MARK', align: 'center' });
    const titleAt = sectionTitlePos(sec, len, ext);
    const top = titleAt ? W(titleAt) : null;
    if (top) lines.push({ kind: 'text', pos: { x: top.x, y: top.y - H }, height: H, text: `${label}-${label}`, layer: 'MARK', align: 'center' });
  }
  return { solids, lines };
}
